<template>
  <DrawerPanel :model-value="isOpen" :title="resolvedAlert?.title ?? 'Alert'" @update:model-value="close">
    <div class="alert-detail-drawer__body">
      <template v-if="resolvedAlert">
        <div class="alert-detail-drawer__description" data-qa="alert-detail-drawer-description"
          v-html="resolvedAlert.description"></div>

        <component
          :is="resolvedAlertCtaTag"
          v-bind="resolvedAlertCtaProps"
          class="alert-detail-drawer__cta-link"
          data-qa="alert-detail-drawer-cta"
          @click="onCtaClick"
        >
          <BaseButton class="alert-detail-drawer__cta-button" element="span">
            {{ resolvedAlertCta.text }}
          </BaseButton>
        </component>
      </template>

      <p v-else-if="alertsStore.isLoading || !alertsStore.hasLoaded" class="alert-detail-drawer__status"
        data-qa="alert-detail-drawer-loading">
        Loading alert...
      </p>

      <p v-else-if="alertsStore.isError || alertsStore.alerts === null" class="alert-detail-drawer__status"
        data-qa="alert-detail-drawer-error">
        We couldn't load this alert right now.
      </p>

      <p v-else-if="showNotFound" class="alert-detail-drawer__not-found" data-qa="alert-detail-drawer-not-found">
        Sorry, we couldn't find the alert you're looking for.
      </p>
    </div>
  </DrawerPanel>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

import BaseButton from '@/components/base/BaseButton.vue';
import DrawerPanel from '@/components/drawer-panel/DrawerPanel.vue';
import { useAlertsStore } from '@/stores';
import { AlertWrapper } from '@/types/responseTypes';
import { resolveAlertCta } from '@/utils/alertCta';


const route = useRoute();
const router = useRouter();
const alertsStore = useAlertsStore();

const isOpen = computed(() => alertsStore.selectedAlertId !== null);
const resolvedAlert = computed<AlertWrapper | undefined>(() => alertsStore.selectedAlert);
const resolvedAlertCta = computed(() => resolveAlertCta(resolvedAlert.value, router));
const resolvedAlertCtaTag = computed(() => ('to' in resolvedAlertCta.value ? RouterLink : 'a'));
const resolvedAlertCtaProps = computed(() =>
  'to' in resolvedAlertCta.value
    ? { to: resolvedAlertCta.value.to }
    : { href: resolvedAlertCta.value.href },
);
const showNotFound = computed(() =>
  isOpen.value && alertsStore.alerts !== null && !resolvedAlert.value,
);

watch(
  () => route.query.alertDetail,
  (alertDetail) => {
    const alertId = Number(alertDetail);
    if (!alertId) return;

    alertsStore.openAlertDetail(alertId);

    const query = { ...route.query };
    delete query.alertDetail;
    router.replace({ query });
  },
  { immediate: true },
);

const close = () => {
  alertsStore.closeAlertDetail();
};

const onCtaClick = () => {
  close();
};
</script>

<style scoped lang="scss">
.alert-detail-drawer {
  &__body {
    padding: 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__icon-row {
    display: flex;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: $brand-secondary-100-light-blue;
    flex-shrink: 0;
  }

  &__date {
    color: $blue-33;
    font-size: remc(14px);
    line-height: remc(20px);
    font-family: $helvetica;
    margin: 0;
  }

  &__description {
    color: $brand-primary-100-navy;
    font-size: remc(16px);
    line-height: remc(24px);
    margin: 0;

    :deep(p) {
      margin-bottom: 1.5rem;
    }
  }

  &__cta-link {
    text-decoration: none;
  }

  &__cta-button {
    width: 100%;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 16px;
    background: $light-navy-5;
    border-radius: 8px;
  }

  &__detail-row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  &__detail-name {
    color: $brand-secondary-100-light-navy;
    font-size: remc(14px);
    line-height: remc(20px);
    font-family: $helvetica
  }

  &__detail-value {
    color: $brand-primary-100-navy;
    font-size: remc(14px);
    line-height: remc(20px);
    font-family: $helvetica;
    font-weight: 700;
    margin: 0;
    text-align: right;
  }

  &__not-found {
    color: $brand-secondary-100-light-navy;
    font-size: remc(16px);
    line-height: remc(24px);
    text-align: center;
    margin: 0;
    padding: 32px 0;
  }

  &__status {
    color: $brand-secondary-100-light-navy;
    font-size: remc(16px);
    line-height: remc(24px);
    text-align: center;
    margin: 0;
    padding: 32px 0;
  }
}
</style>
